import React, { useEffect, useState } from 'react'
import SimpleBar from 'simplebar-react'
import { XClose, Download04, AlertCircle, ChevronDown } from '@untitled-ui/icons-react'
import { MyButton, MyTextField, MyDropzone, myToaster } from '@interstellar-component'
import { CompanyService } from '../../service'
import { useCompany } from '../../Context'

// Accept set aligned with the BE companyReviseUpload allowlist
// (pdf/doc/docx/png/jpg/jpeg) so the upload isn't bounced server-side.
const DOC_ACCEPT = ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg']

// The resend-form blocks, in display order. `key` matches the reviewer reason
// `field` (core REVISION_REASONS) so we can show only the flagged ones. `nib` is
// the text field; the rest are dropzones. `fileKey` is the FormData/file-state
// key — note surat_kuasa -> authority_letter, matching the BE upload field name.
const FIELD_BLOCKS = [
  { key: 'npwp_doc', type: 'file', label: 'NPWP', fileKey: 'npwp_doc' },
  { key: 'nib', type: 'nib', label: 'NIB / Business Registration Number' },
  { key: 'akta_pendirian', type: 'file', label: 'Akta Pendirian', fileKey: 'akta_pendirian' },
  { key: 'akta_perubahan_terbaru', type: 'file', label: 'Akta Perubahan Terbaru', fileKey: 'akta_perubahan_terbaru' },
  { key: 'surat_kuasa', type: 'file', label: 'Surat kuasa', fileKey: 'authority_letter', hasTemplate: true },
]

// "Reject & resend" form (Figma node 12972-20099). Slide-over beside the detail
// slider, opened from the rejected-application panel (Result tab). The reviewer
// flags specific items (structured reason codes -> verification.reasons), so the
// form only shows the flagged fields by default; the rest sit behind an optional
// "Edit other documents" disclosure. Submitting PATCHes /company/:id/revise.
// Documents the client doesn't re-upload are kept by the BE. When a rejection has
// no structured field reasons (legacy / general-only), the full form is shown.
function ResendApplicationPanel({ companyId, onClose }) {
  const { fetchCompanyDetail } = useCompany()
  const [loading, setLoading] = useState(true)
  const [info, setInfo] = useState(null)
  const [nib, setNib] = useState('')
  const [files, setFiles] = useState({
    npwp_doc: null,
    akta_pendirian: null,
    akta_perubahan_terbaru: null,
    authority_letter: null,
  })
  const [showMore, setShowMore] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // One fetch gives the current NIB + the reviewer's per-field reasons.
  useEffect(() => {
    if (!companyId) return
    setLoading(true)
    CompanyService.getDetailCompany(companyId, { type: 'information' })
      .then((res) => {
        const d = res?.data !== undefined ? res.data : res
        setInfo(d)
        setNib(d?.compliance_documents?.nib ?? '')
      })
      .catch(myToaster)
      .finally(() => setLoading(false))
  }, [companyId])

  const reasons = info?.verification?.reasons ?? []
  const note = info?.verification?.note
  const reasonFor = (field) => reasons.find((r) => r.field === field)?.message || null
  const generalReasons = reasons.filter((r) => !r.field)

  // Fields the reviewer flagged. When at least one field is flagged we show only
  // those (the others go behind the optional disclosure). With no field-specific
  // reasons (only a free-text note or a general reason) we fall back to the full
  // form so the client always has something they can fix.
  const flaggedFields = new Set(reasons.filter((r) => r.field).map((r) => r.field))
  const hasStructured = flaggedFields.size > 0
  const primaryBlocks = hasStructured
    ? FIELD_BLOCKS.filter((b) => flaggedFields.has(b.key))
    : FIELD_BLOCKS
  const otherBlocks = hasStructured ? FIELD_BLOCKS.filter((b) => !flaggedFields.has(b.key)) : []

  const pickFile = (key) => (fileList) =>
    setFiles((prev) => ({ ...prev, [key]: fileList && fileList.length > 0 ? fileList[0] : null }))

  const handleDownloadTemplate = () => {
    const url = CompanyService.suratKuasaTemplate()
    if (url) window.open(url, '_blank')?.focus()
  }

  const handleSubmit = async () => {
    if (!companyId || submitting) return
    if (!nib.trim()) {
      myToaster({ message: 'NIB / Business Registration Number is required' })
      return
    }
    // The flagged documents must actually be re-uploaded — resubmitting the same
    // doc the reviewer rejected would just bounce again. Optional ("other")
    // documents stay optional and keep their existing file when left empty.
    if (hasStructured) {
      const missing = primaryBlocks
        .filter((b) => b.type === 'file' && !(files[b.fileKey] instanceof File))
        .map((b) => b.label)
      if (missing.length > 0) {
        myToaster({ message: `Please re-upload the flagged document(s): ${missing.join(', ')}` })
        return
      }
    }
    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('nib', nib.trim())
      if (files.npwp_doc instanceof File) fd.append('npwp_doc', files.npwp_doc)
      if (files.akta_pendirian instanceof File) fd.append('akta_pendirian', files.akta_pendirian)
      if (files.akta_perubahan_terbaru instanceof File) {
        fd.append('akta_perubahan_terbaru', files.akta_perubahan_terbaru)
      }
      if (files.authority_letter instanceof File) fd.append('authority_letter', files.authority_letter)

      const res = await CompanyService.reviseCompany(companyId, fd)
      myToaster(res)
      // Refresh the slider so the header status flips back to "Waiting approval".
      await fetchCompanyDetail(companyId, { type: 'result' })
      onClose()
    } catch (err) {
      myToaster(err)
    } finally {
      setSubmitting(false)
    }
  }

  const FieldError = ({ field }) => {
    const msg = reasonFor(field)
    if (!msg) return null
    return <p className="mt-1.5 text-sm-regular text-error/600">{msg}</p>
  }

  // Renders one form block. `required` only drives the visual `*` — file fields
  // are enforced in handleSubmit (flagged ones only); NIB is always required.
  const renderBlock = (block, { required }) => {
    const star = required ? "after:text-error/600 after:content-['*']" : ''
    if (block.type === 'nib') {
      return (
        <div key={block.key} className="flex flex-col gap-1.5">
          <label className={`text-sm-medium text-gray-700 ${star}`}>{block.label}</label>
          <MyTextField
            name="nib"
            value={nib}
            onChangeForm={(e) => setNib(e.target.value)}
            placeholder="e.g. 1901 2200 0928 2"
            margin="7px 12px"
          />
          <FieldError field="nib" />
        </div>
      )
    }
    return (
      <div key={block.key} className="flex flex-col gap-1.5">
        <label className={`text-sm-medium text-gray-700 ${star}`}>{block.label}</label>
        {block.hasTemplate ? (
          <div className="w-fit">
            <MyButton
              variant="outlined"
              color="purple"
              type="button"
              size="md"
              onClick={handleDownloadTemplate}
            >
              <Download04 className="size-5" stroke="currentColor" />
              <span className="text-sm-semibold">Download Template</span>
            </MyButton>
          </div>
        ) : null}
        <MyDropzone
          colorBg="bg-white"
          multiple={false}
          accept={DOC_ACCEPT}
          maxSize={5242880}
          onChange={pickFile(block.fileKey)}
        />
        {/* The reviewer's rejection reason is fetched once at mount and is not
            reactive to uploads, so it lingered after the user picked a valid
            replacement (Everest #z94x8e25vg). Hide it once a new file is chosen
            — same predicate handleSubmit uses to gate the resubmit. */}
        {!(files[block.fileKey] instanceof File) ? <FieldError field={block.key} /> : null}
      </div>
    )
  }

  return (
    <div className="flex h-screen w-[420px] flex-col bg-white shadow-xl">
      <header className="relative flex items-start gap-x-4 border-b border-gray-100 px-6 py-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-[12px] top-[12px] flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={20} stroke="currentColor" />
        </button>
        <div className="flex flex-1 flex-col gap-1 pt-1">
          <p className="text-lg font-semibold text-gray-900">Reject &amp; resend</p>
          <p className="text-sm font-medium text-gray-500">
            Correct the items the reviewer flagged, then resubmit for review.
            Documents you don&apos;t re-upload are kept.
          </p>
        </div>
      </header>

      <section className="flex-1 overflow-hidden">
        <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
          <div className="flex flex-col gap-6 px-6 py-6">
            {loading ? (
              <div className="h-32 animate-pulse rounded-md bg-gray-light/100" />
            ) : (
              <>
                {note || generalReasons.length > 0 ? (
                  <div className="flex items-start gap-2 rounded-xl border border-error/200 bg-error/50 px-4 py-3">
                    <AlertCircle
                      className="mt-0.5 size-[18px] shrink-0 text-error/600"
                      stroke="currentColor"
                      aria-hidden
                    />
                    <div className="flex flex-col gap-1">
                      {note ? (
                        <p className="whitespace-pre-line text-sm-regular text-error/700">{note}</p>
                      ) : null}
                      {generalReasons.map((r) => (
                        <p key={r.code} className="text-sm-regular text-error/700">
                          {r.message}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Star only where the field is actually enforced (handleSubmit):
                    NIB always; documents only on the structured/flagged path. In
                    the legacy path the reviewer flagged no field, so un-reuploaded
                    docs are kept — showing a `*` there was a false requirement. */}
                {primaryBlocks.map((b) =>
                  renderBlock(b, { required: b.type === 'nib' || hasStructured }),
                )}

                {otherBlocks.length > 0 ? (
                  <div className="flex flex-col gap-6 border-t border-gray-100 pt-6">
                    <button
                      type="button"
                      onClick={() => setShowMore((v) => !v)}
                      className="flex items-center gap-1.5 text-sm-semibold text-gray-600 hover:text-gray-800"
                    >
                      <ChevronDown
                        className={`size-4 transition-transform ${showMore ? 'rotate-180' : ''}`}
                        stroke="currentColor"
                        aria-hidden
                      />
                      {showMore ? 'Hide other documents' : 'Edit other documents (optional)'}
                    </button>
                    {showMore ? otherBlocks.map((b) => renderBlock(b, { required: false })) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </SimpleBar>
      </section>

      <footer className="flex items-center gap-3 border-t border-gray-100 px-6 py-4">
        <div className="flex-1">
          <MyButton
            color="secondary"
            variant="outlined"
            size="md"
            expanded
            type="button"
            disabled={submitting}
            onClick={onClose}
          >
            <span className="text-sm-semibold">Cancel</span>
          </MyButton>
        </div>
        <div className="flex-1">
          <MyButton
            color="primary"
            variant="filled"
            size="md"
            expanded
            type="button"
            disabled={loading || submitting}
            onClick={handleSubmit}
          >
            <span className="text-sm-semibold text-white">
              {submitting ? 'Resubmitting…' : 'Resubmit for review'}
            </span>
          </MyButton>
        </div>
      </footer>
    </div>
  )
}

export default ResendApplicationPanel
